import { useCallback, useEffect, useState } from 'react';
import { api, Badge, ErrorAlert, formatTime, usePolling } from '@pryme/ui';
import { EventList } from '../components/EventList';
import { useServiceOutlet } from '../components/ServiceLayout';
import { EVENT_FILTERS, isFailure, matchesFilter, type EventFilter } from '../lib/events';
import type { DevEvent } from '../lib/types';

export function Events() {
  const { service: s } = useServiceOutlet();
  const [events, setEvents] = useState<DevEvent[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [filter, setFilter] = useState<EventFilter>('all');
  const [live, setLive] = useState(true);
  const [updated, setUpdated] = useState<string | null>(null);
  const [fresh, setFresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api<{ events: DevEvent[] }>(`/v1/services/${s.id}/events`);
      setEvents((prev) => {
        const top = prev[0]?.id ?? 0;
        if (prev.length) setFresh(r.events.filter((e) => e.id > top).length);
        return r.events;
      });
      setUpdated(new Date().toISOString());
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [s.id]);

  useEffect(() => {
    setEvents([]);
    void load();
  }, [load]);
  usePolling(() => void load(), 3000, live);

  const shown = events.filter((e) => matchesFilter(e, filter));
  const counts = Object.fromEntries(EVENT_FILTERS.map((f) => [f.id, events.filter((e) => matchesFilter(e, f.id)).length]));

  return (
    <section className="p-card" aria-labelledby="ev-h">
      <div className="p-row p-between">
        <h2 id="ev-h">Live events</h2>
        <div className="p-row">
          <span className="p-row p-small p-muted" aria-live="polite" style={{ gap: '0.5rem' }}>
            <span className={`p-status ${live ? 'ok' : ''}`}>{live ? 'Live' : 'Paused'}</span>
            <span>updated {formatTime(updated)}</span>
            {fresh > 0 && <Badge tone="brand">{fresh} new</Badge>}
          </span>
          <button className="p-btn secondary small" onClick={() => setLive((l) => !l)} aria-pressed={!live}>{live ? 'Pause' : 'Resume'}</button>
          <button className="p-btn ghost small" onClick={() => void load()}>Refresh</button>
        </div>
      </div>
      <p className="p-small p-muted">
        Failed signatures, replays, domain verification, key changes, sign-ins, consent, token exchange, verification and mandate decisions. No personal identifiers or claim
        values are logged. Last 100 events.
      </p>
      <div className="dv-filters" role="group" aria-label="Filter events">
        {EVENT_FILTERS.map((f) => (
          <button key={f.id} aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
            {f.label} <span className="p-muted">({counts[f.id]})</span>
          </button>
        ))}
      </div>
      {events.some(isFailure) && filter === 'all' && (
        <p className="p-small"><Badge tone="bad">{events.filter(isFailure).length} failures</Badge> in the last {events.length} events.</p>
      )}
      <ErrorAlert error={error} />
      <EventList events={shown} />
    </section>
  );
}
